class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onRecommendationClick.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  onRecommendationClick(event) {
    const toggleTarget = event.target.closest('[data-cart-recs-toggle]');
    if (toggleTarget) {
      const wrap = toggleTarget.closest('.cart-drawer-recommendations');
      if (wrap) {
        const collapsed = wrap.classList.toggle('is-collapsed');
        toggleTarget.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      }
      return;
    }

    const previousButton = event.target.closest('[data-cart-recommendations-prev]');
    const nextButton = event.target.closest('[data-cart-recommendations-next]');
    const addButton = event.target.closest('.cart-drawer-recommendations__button');

    if (previousButton || nextButton) {
      const track = event.target.closest('.cart-drawer-recommendations')?.querySelector('.cart-drawer-recommendations__track');
      if (!track) return;

      event.preventDefault();
      track.scrollBy({
        left: (nextButton ? 1 : -1) * Math.max(track.clientWidth * 0.8, 220),
        behavior: 'smooth',
      });
      return;
    }

    if (!addButton) return;

    event.preventDefault();
    const variantId = addButton.dataset.variantId;
    if (!variantId || addButton.getAttribute('aria-busy') === 'true') return;

    addButton.setAttribute('aria-busy', 'true');
    addButton.disabled = true;

    const sections = this.getSectionsToRender().map((section) => section.id);
    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections,
      sections_url: window.location.pathname,
    });

    fetch(routes.cart_add_url, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) {
          const errors = document.getElementById('CartDrawer-CartErrors');
          if (errors) errors.textContent = state.description || window.cartStrings.error;
          return;
        }

        this.renderContents(state);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-recommendations', cartData: state, variantId });
      })
      .catch(() => {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        addButton.removeAttribute('aria-busy');
        addButton.disabled = false;
      });
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
